import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Preview/Sidebar';
import HelpButton from '../FeedbackButton/FeedbackButton';
import './main.css';
import '../MessageHub/MessageHub.css';
import { useGlobalRealtime } from '../../hooks/useGlobalRealtime';
import { useInactivityLogout } from '../../hooks/useInactivityLogout';
import { useSessionStore } from '../../store/sessionStore';
import { ConnectionStatusBanner } from '../ui/ConnectionStatusBanner';
import ProfileSectionNudgeHost from '../ProfileSectionNudgeCard/ProfileSectionNudgeHost';
import { ProfileAccessRequestModal } from '../Dashboard/ProfileAccessRequestModal';

const MainContainer: React.FC = () => {
  const location = useLocation();
  const noSidebarRoutes = ['/onboarding', '/get-started', '/create-prompt'];

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userData = useSessionStore((s) => s.currentUser) as {
    profileImg?: string;
  } | null;
  const prompts = useSessionStore((s) => s.prompts) as unknown[];
  const sessionStatus = useSessionStore((s) => s.status);
  const loading = sessionStatus === 'idle' || sessionStatus === 'loading';

  useGlobalRealtime();
  useInactivityLogout(Boolean(localStorage.getItem('accessToken')));

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      void useSessionStore
        .getState()
        .bootstrapSession()
        .catch(() => {
          /* session store keeps cached data and surfaces connectionMessage */
        });
    }
  }, []);

  const shouldHideSidebar = () => {
    // Always hide for these routes
    if (noSidebarRoutes.includes(location.pathname)) return true;

    // Special case for avatar completion page
    if (location.pathname === '/complete-profile-avatar') {
      if (loading) return true; // hide while still loading

      const hasProfilePhoto = !!userData?.profileImg;
      const hasPrompts = prompts.length > 0;

      // Hide sidebar if either is missing
      return !(hasProfilePhoto && hasPrompts);
    }

    return false; // default: show sidebar
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /** Close overlay sidebar after any navigation (e.g. Home → Dashboard). */
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const sidebarVisible = !shouldHideSidebar() && !isMobile;
  const isMessagesHub = location.pathname.startsWith('/messages');
  const isPromptsPage =
    location.pathname === '/prompts' || location.pathname.startsWith('/prompts/');
  const isProfilePage =
    location.pathname === '/user-profile' || location.pathname.startsWith('/user-profile/');
  const isCreatePromptPage = location.pathname === '/create-prompt';
  const hideAppHeader = location.pathname === '/onboarding';
  const showHelpButton =
    !!localStorage.getItem('accessToken') &&
    !isMessagesHub &&
    !['/onboarding', '/get-started', '/complete-profile', '/create-prompt'].includes(
      location.pathname
    );
  const useFullWidthHeaderOffset = shouldHideSidebar() && !isMobile && !hideAppHeader;

  return (
    <div
      className={`main-layout ${isMessagesHub ? 'main-layout--message-hub' : ''} ${useFullWidthHeaderOffset ? 'main-layout--full-width-header' : ''} ${hideAppHeader ? 'main-layout--onboarding' : ''}`}
    >
      {!hideAppHeader ? (
        <Header
          onToggleSidebar={toggleSidebar}
          fullWidthHeader={shouldHideSidebar() && !isMobile}
        />
      ) : null}
      <ConnectionStatusBanner />
      {localStorage.getItem('accessToken') ? <ProfileSectionNudgeHost /> : null}
      <div className={`content-container ${isMessagesHub ? 'content-container--message-hub' : ''}`}>
        {/* {!shouldHideSidebar() && (
          <Sidebar
            isOpen={sidebarOpen}
            onClose={closeSidebar}
            userData={userData}
            onTriggerUpdateFlow={() => {}}
          />
        )} */}
        {!shouldHideSidebar() && isMobile && sidebarOpen ? (
          <button
            type="button"
            className="v2-sidebar-backdrop"
            aria-label="Close navigation menu"
            onClick={closeSidebar}
          />
        ) : null}
        {!shouldHideSidebar() && (!isMobile || sidebarOpen) && (
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        )}
        <main
          className={`main-content ${sidebarVisible ? 'main-content-with-sidebar' : ''} ${isMessagesHub ? 'main-content--message-hub' : ''} ${isPromptsPage ? 'main-content--prompts' : ''} ${isProfilePage ? 'main-content--profile' : ''} ${isCreatePromptPage ? 'main-content--create-prompt' : ''}`}
        >
          <Outlet />
        </main>
      </div>
      {showHelpButton ? <HelpButton /> : null}
      {localStorage.getItem('accessToken') ? <ProfileAccessRequestModal /> : null}
    </div>
  );
};

export default MainContainer;
